import moment from 'moment';
import _ from 'lodash';
import TANKS from '../constants/tanks.js';

const fallbackTankId = TANKS.ROTH_275.key;

const Calculations = {
  getStatsForDevice: (device) => {
    if (!device || _.isUndefined(device.lastReadingDistance)) {
      return {
        gallonsRemaining: null,
        percentRemaining: null,
        fillCapacity: Calculations.getActualCapacity(device),
      };
    }
    const reading = { dist: device.lastReadingDistance };
    return {
      gallonsRemaining: Calculations.calculateRemainingGallons(reading, device),
      percentRemaining: Calculations.calculateRemainingPercent(reading, device),
      fillCapacity: Calculations.getActualCapacity(device),
    };
  },
  sortByTimeField: (field) => (a, b) => {
    if (a[field] && !b[field]) {
      return -1;
    } else if (b[field] && !a[field]) {
      return 1;
    }
    return moment(a[field]).isBefore(moment(b[field])) ? -1 : 1;
  },
  getTankOffset: (device, tank) => {
    if (_.isNumber(device.tankOffset)) {
      return device.tankOffset;
    }
    if (_.isNumber(tank.offset)) {
      return tank.offset;
    }
    return 0;
  },
  getTank: (device) => {
    const tankId = device.tankId;
    return TANKS[tankId] || TANKS[fallbackTankId];
  },
  getActualCapacity: (device) => {
    if (!device || !TANKS[device.tankId]) {
      return null;
    }
    const tank = TANKS[device.tankId];
    const capacity = device.actualCapacity || tank.actualCapacity;
    if (capacity > tank.gallons || !device.isTethered) {
      return capacity;
    }
    return capacity * 2;
  },
  calculateRemainingGallons: (reading = {}, device = {}) => {
    const { dist } = reading;
    if (_.isUndefined(dist)) {
      return '--';
    }
    const tank = Calculations.getTank(device);
    const isTethered = device.isTethered;
    const tankOffset = Calculations.getTankOffset(device, tank);
    const value = _.toNumber(
      tank.calculateGallonsRemaining(dist, tankOffset).toFixed(2),
    );
    return isTethered ? value * 2 : value;
  },
  calculateRemainingPercent: (reading = {}, device = {}) => {
    const { dist } = reading;
    if (_.isUndefined(dist)) {
      return '--';
    }
    const actualCapacity = Calculations.getActualCapacity(device);
    const gallonsRemaining = Calculations.calculateRemainingGallons(
      reading,
      device,
    );
    if (gallonsRemaining > actualCapacity) {
      return 100;
    }
    return Math.round((gallonsRemaining * 100) / actualCapacity);
  },
  // Helper function: Calculate Median Absolute Deviation (MAD)
  calculateMAD: (values) => {
    if (!values || values.length === 0) return 0;
    const median = _.sortBy(values)[Math.floor(values.length / 2)];
    const absoluteDeviations = values.map((v) => Math.abs(v - median));
    const mad =
      _.sortBy(absoluteDeviations)[Math.floor(absoluteDeviations.length / 2)];
    return mad;
  },
  // Helper function: Calculate Modified Z-Score using MAD
  calculateModifiedZScore: (value, values) => {
    if (!values || values.length === 0) return 0;
    const median = _.sortBy(values)[Math.floor(values.length / 2)];
    const mad = Calculations.calculateMAD(values);
    // Avoid division by zero
    if (mad === 0) return 0;
    // Modified Z-score = 0.6745 * (value - median) / MAD
    return (0.6745 * Math.abs(value - median)) / mad;
  },
  // Main anomaly detection function
  detectAnomalousReadings: (sortedReadings) => {
    if (!sortedReadings || sortedReadings.length < 3) {
      // Not enough data to detect anomalies
      return sortedReadings.map((r) => ({ ...r, isAnomalous: false }));
    }

    const REFILL_THRESHOLD = 15; // gallons
    const MAX_CONSUMPTION_PER_READING = 12.5; // gallons (~50 gal/day at 4hr intervals)
    const MAX_DAILY_CONSUMPTION = 10; // gallons per day for sparse readings
    const MAD_THRESHOLD = 3.5; // Modified Z-score threshold
    const WINDOW_SIZE = 5;

    // Calculate differences between consecutive readings
    const differences = [];
    for (let i = 1; i < sortedReadings.length; i++) {
      differences.push({
        index: i,
        diff:
          sortedReadings[i].gallonsRemaining -
          sortedReadings[i - 1].gallonsRemaining,
      });
    }

    // Pass 1: Identify refills (large positive jumps)
    const refillIndices = new Set();
    differences.forEach(({ index, diff }) => {
      if (diff >= REFILL_THRESHOLD) {
        refillIndices.add(index);
      }
    });

    // Initialize anomaly flags
    const anomalyFlags = new Array(sortedReadings.length).fill(false);

    // Pass 2 & 3: Statistical outlier and rate-of-change validation
    for (let i = 1; i < sortedReadings.length; i++) {
      // Skip refills - they're legitimate large jumps
      if (refillIndices.has(i)) continue;
      const currentReading = sortedReadings[i];
      const previousReading = sortedReadings[i - 1];

      const diff =
        currentReading.gallonsRemaining - previousReading.gallonsRemaining;

      // Calculate time difference between readings
      const timeDiffHours = moment(currentReading.createdAt).diff(
        moment(previousReading.createdAt),
        'hours',
        true,
      );

      // For sparse readings (>1 day apart), use time-based threshold
      // For frequent readings, use fixed threshold
      const maxConsumption =
        timeDiffHours > 24
          ? (timeDiffHours / 24) * MAX_DAILY_CONSUMPTION
          : MAX_CONSUMPTION_PER_READING;

      // Flag impossible consumption rates (too much consumption in one reading)
      if (diff < -maxConsumption) {
        console.log(
          `impossible consumption rate ${diff} on ${currentReading.createdAt} (too much consumption in one reading)`,
          { maxConsumption, timeDiffHours },
        );
        anomalyFlags[i] = true;
        continue;
      }

      // Flag unexpected increases (not large enough to be refills)
      if (diff > 2 && diff < REFILL_THRESHOLD) {
        console.info(
          `unexpected increase ${diff} (too small for refill)`,
          `\nfrom ${previousReading.gallonsRemaining} to ${currentReading.gallonsRemaining}`,
          `\n${timeDiffHours} hours apart on ${moment(
            currentReading.createdAt,
          ).format('MM/DD/YYYY h:mm a')}`,
        );
        anomalyFlags[i] = true;
        continue;
      }

      // Statistical outlier detection using local window
      const windowStart = Math.max(0, i - WINDOW_SIZE);
      const windowEnd = Math.min(sortedReadings.length, i + WINDOW_SIZE + 1);
      const windowReadings = sortedReadings.slice(windowStart, windowEnd);

      // Only check non-refill readings in the window
      const windowValues = windowReadings
        .map((r, idx) => ({
          value: r.gallonsRemaining,
          globalIdx: windowStart + idx,
        }))
        .filter(({ globalIdx }) => !refillIndices.has(globalIdx))
        .map(({ value }) => value);

      if (windowValues.length >= 3) {
        const zScore = Calculations.calculateModifiedZScore(
          currentReading.gallonsRemaining,
          windowValues,
        );
        if (zScore > MAD_THRESHOLD) {
          console.info(
            'statistical outlier',
            currentReading.createdAt,
            zScore,
            MAD_THRESHOLD,
          );
          anomalyFlags[i] = true;
        }
      }
    }

    // Pass 4: Detect isolated spikes (value differs significantly from both neighbors)
    for (let i = 1; i < sortedReadings.length - 1; i++) {
      const currentReading = sortedReadings[i];
      const previousReading = sortedReadings[i - 1];
      const nextReading = sortedReadings[i + 1];
      if (anomalyFlags[i] || refillIndices.has(i) || refillIndices.has(i + 1))
        continue;

      const prev = previousReading.gallonsRemaining;
      const current = currentReading.gallonsRemaining;
      const next = nextReading.gallonsRemaining;

      // Check if current reading is an isolated spike
      const diffFromPrev = Math.abs(current - prev);
      const diffFromNext = Math.abs(current - next);
      const prevToNextDiff = Math.abs(next - prev);

      // If current differs significantly from both neighbors,
      // but neighbors are similar to each other, it's likely a spike
      if (diffFromPrev > 10 && diffFromNext > 10 && prevToNextDiff < 5) {
        console.info('isolated spike', currentReading.createdAt, {
          current,
          prev,
          next,
        });
        anomalyFlags[i] = true;
      }
    }

    // Return readings with anomaly flags
    return sortedReadings.map((reading, i) => ({
      ...reading,
      isAnomalous: anomalyFlags[i],
    }));
  },
  getNormalizedReadings: (
    readings,
    device,
    { startDate = null, endDate = null, includeAnomalies = false } = {},
  ) => {
    // First, filter and sort readings
    const filteredAndSorted = readings
      .filter((reading) => {
        if (!reading.dist || !reading.createdAt) return false;

        const readingDate = moment(reading.createdAt);

        if (startDate && endDate) {
          return readingDate.isBetween(
            moment(startDate).startOf('day'),
            moment(endDate).endOf('day'),
          );
        }
        if (startDate) {
          return readingDate.isAfter(moment(startDate).startOf('day'), 'day');
        }

        // If no date filters provided, include all readings
        return true;
      })
      .sort(Calculations.sortByTimeField('createdAt'))
      .map((reading) => ({
        ...reading,
        created: moment(reading.createdAt).format('L'),
        date: moment(reading.createdAt).format('MM/DD/YYYY'),
        gallonsRemaining: Calculations.calculateRemainingGallons(
          reading,
          device,
        ),
        percentRemaining: Calculations.calculateRemainingPercent(
          reading,
          device,
        ),
      }));

    // Apply anomaly detection
    const readingsWithAnomalyFlags =
      Calculations.detectAnomalousReadings(filteredAndSorted);

    // Filter out anomalous readings by default, but allow including them for debugging
    if (includeAnomalies) {
      return readingsWithAnomalyFlags;
    }
    return readingsWithAnomalyFlags.filter((reading) => !reading.isAnomalous);
  },
  readingsByDay: (normalizedReadings) => {
    const yesterday = moment().subtract(1, 'day').startOf('day');
    const readingsByDay = _.map(
      _.groupBy(normalizedReadings, 'created'),
      (readingsForDay, dateKey) => {
        // ensure we return the latest reading for recent dates
        if (moment(dateKey, 'L').isAfter(yesterday)) {
          const sortedReadingsForDay = readingsForDay
            .sort(Calculations.sortByTimeField('createdAt'))
            .reverse();
          return sortedReadingsForDay[0];
        }
        const mostGallonsRemainingReading = _.maxBy(
          readingsForDay,
          'gallonsRemaining',
        );
        const leastGallonsRemainingReading = _.minBy(
          readingsForDay,
          'gallonsRemaining',
        );
        if (mostGallonsRemainingReading - leastGallonsRemainingReading > 15) {
          return mostGallonsRemainingReading;
        }
        return leastGallonsRemainingReading;
      },
    );
    return readingsByDay.map((readingForDay, i) => {
      let consumption = 0;
      if (i > 0 && readingsByDay[i - 1]) {
        consumption =
          readingsByDay[i - 1].gallonsRemaining -
          readingForDay.gallonsRemaining;
      }
      return _.omit(
        {
          ...readingForDay,
          consumption,
          date: moment(readingForDay.createdAt).format('MM/DD/YYYY'),
        },
        ['updatedAt'],
      );
    });
  },
  calculateStats: (readings, startDate, device) => {
    const normalizedReadings = Calculations.getNormalizedReadings(
      readings,
      device,
      { startDate },
    );
    const readingsByDay = Calculations.readingsByDay(normalizedReadings);
    const latestReading = readingsByDay[readingsByDay.length - 1];
    const totalConsumption = Math.round(
      _.sumBy(
        readingsByDay.filter(({ consumption }) => consumption > 0),
        'consumption',
      ),
    );
    const averageConsumption = Math.round(
      totalConsumption / readingsByDay.length,
    );
    const gallonsRemaining = Calculations.calculateRemainingGallons(
      latestReading,
      device,
    );
    const percentRemaining = Calculations.calculateRemainingPercent(
      latestReading,
      device,
    );
    const latestReadingDate = latestReading
      ? moment(new Date(latestReading.createdAt))
      : null;
    const actualCapacity = Calculations.getActualCapacity(device);
    let refillAmount =
      gallonsRemaining !== null ? actualCapacity - gallonsRemaining : '';
    if (_.isNumber(refillAmount) && refillAmount < 0) {
      refillAmount = 0;
    }
    return {
      averageConsumption,
      totalConsumption,
      gallonsRemaining,
      percentRemaining,
      latestReadingDate,
      readingsByDay,
      normalizedReadings,
      refillAmount,
      actualCapacity,
    };
  },
  dataForGraphs: (readings, device, timeFrame, showConsumption, startDate) => {
    return new Promise((resolve) => {
      if (!showConsumption) {
        const data = Calculations.calculateStats(readings, startDate, device);
        return resolve(data);
      }

      let readingsForPeriod;
      let data = Calculations.calculateStats(readings, startDate, device);
      if (timeFrame === 7) {
        readingsForPeriod = data.readingsByDay;
      } else if (timeFrame === 30) {
        readingsForPeriod = _.values(
          _.groupBy(data.readingsByDay, (reading) =>
            moment(reading.createdAt).endOf('week').format('MM/DD/YYYY'),
          ),
        ).map((readingsByWeek) => ({
          date: moment(readingsByWeek[0].createdAt)
            .endOf('week')
            .format('MM/DD/YYYY'),
          consumption: _.sumBy(
            readingsByWeek.filter(({ consumption }) => consumption > 0),
            'consumption',
          ),
        }));
      } else if (timeFrame === 365) {
        readingsForPeriod = _.values(
          _.groupBy(data.readingsByDay, (reading) =>
            moment(reading.createdAt).endOf('month').format('MM/DD/YYYY'),
          ),
        ).map((readingsByMonth) => ({
          date: moment(readingsByMonth[0].createdAt)
            .endOf('month')
            .format('MM/DD/YYYY'),
          consumption: _.sumBy(
            readingsByMonth.filter(({ consumption }) => consumption > 0),
            'consumption',
          ),
        }));
      }
      data.readingsByDay = _.take(readingsForPeriod, 24);
      return resolve(data);
    });
  },
};

// Named exports for convenience
export const sortByTimeField = Calculations.sortByTimeField;
export const getNormalizedReadings = Calculations.getNormalizedReadings;
export const calculateRemainingGallons = Calculations.calculateRemainingGallons;
export const calculateRemainingPercent = Calculations.calculateRemainingPercent;
export const getActualCapacity = Calculations.getActualCapacity;
export const calculateStats = Calculations.calculateStats;
export const dataForGraphs = Calculations.dataForGraphs;
export const getStatsForDevice = Calculations.getStatsForDevice;
export const detectAnomalousReadings = Calculations.detectAnomalousReadings;

export default Calculations;
